import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PublicShell from "../components/PublicShell";
import api, { formatApiError, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck, ArrowRight, AlertTriangle, Coins, Trophy, Users,
  Building2, Briefcase, Heart, Gavel, GraduationCap, Eye, ScrollText,
} from "lucide-react";

/**
 * /demo-logins
 * --------------------------------------------------------------------------
 * One-click login switcher used during QA, investor walkthroughs and
 * staging demos.  This page surfaces ONLY when:
 *   - the backend env flag `ENABLE_DEMO_LOGIN=1` is set, AND
 *   - the frontend env flag `REACT_APP_DEMO_MODE=true` is set.
 *
 * Either side closes the door in production.
 */

const ROLE_ICON = {
  super_admin: ShieldCheck,
  org_admin: Building2,
  event_manager: Users,
  chair: Gavel,
  school_admin: GraduationCap,
  faculty_advisor: ScrollText,
  volunteer: Users,
  parent: Heart,
  delegate: Trophy,
  sponsor: Briefcase,
  judge: Gavel,
  partner_institution: Eye,
};

const ROLE_DIPLOKS = {
  super_admin: { tone: "gold", label: "Diploks Admin Console" },
  org_admin: { tone: "gold", label: "Full Diploks Wallet" },
  event_manager: { tone: "gold", label: "Full Diploks Wallet" },
  school_admin: { tone: "gold", label: "School Diploks Wallet" },
  chair: { tone: "muted", label: "Diploks context hint only" },
  faculty_advisor: { tone: "muted", label: "Diploks context hint only" },
  parent: { tone: "off", label: "No Diploks access" },
  delegate: { tone: "off", label: "Podium Points, no Diploks" },
  volunteer: { tone: "off", label: "No Diploks access" },
  sponsor: { tone: "off", label: "No Diploks access" },
  judge: { tone: "off", label: "No Diploks access" },
  partner_institution: { tone: "off", label: "No Diploks access" },
};

export default function DemoLoginsPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [enabled, setEnabled] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [groups, setGroups] = useState([]);
  const [working, setWorking] = useState(null);

  const clientFlag = (process.env.REACT_APP_DEMO_MODE || "").toLowerCase() === "true";

  // Hostname gate — demo personas are ONLY ever visible on preview / localhost,
  // never on the production domain even if `REACT_APP_DEMO_MODE` accidentally
  // leaks into a prod build.
  const isPreviewLikeHostname = (() => {
    if (typeof window === "undefined") return false;
    const h = (window.location.hostname || "").toLowerCase();
    return (
      h === "localhost"
      || h === "127.0.0.1"
      || h.endsWith(".preview.emergentagent.com")
      || h.endsWith(".emergent.host")
    );
  })();

  useEffect(() => {
    if (!clientFlag || !isPreviewLikeHostname) {
      setEnabled(false);
      return;
    }
    api.get("/demo/status")
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false));
  }, [clientFlag, isPreviewLikeHostname]);

  useEffect(() => {
    if (!enabled) return;
    api.get("/demo/personas")
      .then((r) => {
        setPersonas(r.data?.personas || []);
        setGroups(r.data?.groups || []);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [enabled]);

  const loginAs = async (persona) => {
    setWorking(persona.email);
    try {
      const { data } = await api.post("/demo/login-as", { email: persona.email });
      setToken(data.access_token);
      await refresh();
      toast.success(`Logged in as ${persona.title}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(null);
    }
  };

  // Disabled state — production-safe rendering.
  if (enabled === false) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div
            data-testid="demo-logins-disabled"
            className="rounded-2xl border border-amber-200 bg-amber-50 p-6 flex items-start gap-4"
          >
            <AlertTriangle className="h-6 w-6 text-amber-700 shrink-0 mt-1" />
            <div>
              <h1 className="text-xl font-bold text-amber-900">
                Demo logins are disabled
              </h1>
              <p className="mt-2 text-sm text-amber-800 leading-relaxed">
                This page is only available in staging / QA environments where
                the operator has explicitly opted in by setting
                <code className="mx-1 px-1 rounded bg-amber-100 font-mono">
                  ENABLE_DEMO_LOGIN=1
                </code>
                on the backend and
                <code className="mx-1 px-1 rounded bg-amber-100 font-mono">
                  REACT_APP_DEMO_MODE=true
                </code>
                on the frontend.  Production deployments are protected by
                default.
              </p>
            </div>
          </div>
        </div>
      </PublicShell>
    );
  }

  if (enabled === null) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 py-16 text-sm text-slate-500">
          Loading demo personas…
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <section
        className="border-b border-slate-200 bg-[#0A192F] text-white"
        data-testid="demo-logins-page"
      >
        <div className="max-w-7xl mx-auto px-6 py-14 lg:py-16">
          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-[#D4AF37]/30 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
            Staging / QA · Demo Mode
          </p>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
            One-click <span className="gold-gradient-text font-serif italic">demo logins</span>
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl text-sm sm:text-base">
            Switch into every Diplomexa role to inspect role-appropriate
            dashboards, Diploks visibility, and Event RBAC scope.  Each
            persona logs in with a real seeded user — production credentials
            are never affected.
          </p>
          <div className="mt-4 text-[11px] text-slate-400 flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
            Demo login is gated by{" "}
            <code className="px-1 rounded bg-white/10 font-mono text-[10px]">
              ENABLE_DEMO_LOGIN=1
            </code>{" "}
            on the backend and{" "}
            <code className="px-1 rounded bg-white/10 font-mono text-[10px]">
              REACT_APP_DEMO_MODE=true
            </code>{" "}
            on the frontend.  Either side disabled blocks this page.
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-12 space-y-10">
          {groups.map((group) => (
            <div key={group} data-testid={`demo-logins-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
              <h2 className="text-xs uppercase tracking-[0.24em] text-slate-500 font-bold">
                {group}
              </h2>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {personas
                  .filter((p) => p.group === group)
                  .map((p) => {
                    const Icon = ROLE_ICON[p.jwt_role] || Users;
                    const dipl = ROLE_DIPLOKS[p.jwt_role] || { tone: "off", label: "—" };
                    return (
                      <div
                        key={p.email}
                        data-testid={`demo-login-card-${p.email}`}
                        className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#D4AF37] hover:shadow-sm transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="h-10 w-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
                            <Icon className="h-5 w-5 text-[#0A192F]" />
                          </div>
                          <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                            JWT · {p.jwt_role}
                          </span>
                        </div>
                        <p className="mt-3 text-sm font-bold text-[#0A192F]">{p.title}</p>
                        <p className="text-[11px] text-slate-500">{p.email}</p>
                        <p className="mt-2 text-[11px] text-slate-700 leading-relaxed line-clamp-3">
                          {p.blurb}
                        </p>
                        <div
                          className={`mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 ${
                            dipl.tone === "gold"
                              ? "bg-[#D4AF37]/15 text-[#0A192F] border border-[#D4AF37]/40"
                              : dipl.tone === "muted"
                              ? "bg-slate-100 text-slate-700"
                              : "bg-rose-50 text-rose-700 border border-rose-100"
                          }`}
                        >
                          <Coins className="h-3 w-3" /> {dipl.label}
                        </div>
                        <button
                          type="button"
                          onClick={() => loginAs(p)}
                          disabled={working === p.email}
                          data-testid={`demo-login-button-${p.email}`}
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white font-semibold text-xs uppercase tracking-wider px-4 py-2.5 rounded-md hover:bg-[#0F2C50] disabled:opacity-50"
                        >
                          {working === p.email ? "Signing in…" : "Login as"}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}

          {personas.length === 0 && (
            <p className="text-center text-sm text-slate-500 py-10">
              No demo personas seeded yet.  Re-run the seeder to surface them.
            </p>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
